@if (noteContentState() === 'ready') {
@if (creationJourney()) {
  <!-- Measured on the whole header, as the saved note's header is: the title row shares its width with the actions,
       which `contracted` folds away, so measuring the title would flip them in and out. -->
  <div class="note-header" (resize)="handleResize($event)">
    <div class="heading">
      @if (canGoBack()) {
        <app-button2 appearance="secondary-ghost" size="s" ariaLabel="Go back" (click)="handleGoBack()">
          <app-icon name="chevron-left" />
        </app-button2>
      }
      <app-input
        class="input"
        appearance="sapphire-inline"
        [inputFormControl]="form.controls.title"
        placeholder="New Note"
        [errorPlaceholder]="'Add title'"
        [withErrors]="submitted"
        size="m"
      />
      @if (showCloseButton) {
        <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" ariaLabel="Close" (click)="handleClose()" />
      }
    </div>

    <div class="creation-toolbar">
      <div class="creation-toolbar-buttons">
        @if (workspaceIdentity(); as identity) {
          <app-button2
            #saveLocationTrigger
            class="creation-toolbar-picker creation-toolbar-workspace"
            appearance="secondary"
            size="m"
            [height]="32"
            rounding="lg"
            [ariaLabel]="saveLocationButtonLabel()"
            [ariaExpanded]="saveLocationMenuOpen()"
            ariaHaspopup="dialog"
            [style.width.px]="saveLocationButtonWidth()"
            [style.--color-border]="saveLocationMenuOpen() ? '#1b5fff' : null"
            stchPopover
            stchPopoverTrigger="click"
            [stchPopoverTemplate]="saveLocationMenu"
            stchPopoverPlacement="bottomLeft"
            [stchPopoverOffsetY]="2"
            [stchPopoverCloseOnOutsideClick]="true"
            [stchPopoverShowUntil]="closeSaveLocationMenu"
            [stchPopoverNoBorder]="true"
            (stchPopoverVisibleChange)="handleSaveLocationMenuVisible($event)"
          >
            @if (destinationIsTeam()) {
              <span class="creation-toolbar-avatar">{{ teamInitials() }}</span>
            } @else {
              <app-icon name="sapphire-lock" />
            }
            <span class="creation-toolbar-workspace-label" [title]="saveLocationButtonLabel()">
              {{ saveLocationButtonLabel() }}
            </span>
            <app-icon name="sapphire-chevron-down" />
          </app-button2>
        }

        <!-- Team workspaces only, as on a saved note: a personal note's notebook is set on its Details tab
             (Johanna, 2026-10-21: no notebook button in New Note). -->
        @if (spaceContextStore.insideTeam()) {
          <ng-container [ngTemplateOutlet]="notebookButton" />
        }
        @if (collaborationControlsTenantId()) {
          <app-note-collaboration-controls
            [noteId]="stitchItem?.id ?? null"
            [teamTenantId]="collaborationControlsTenantId()"
            [teamName]="collaborationTeamName()"
            [draft]="!stitchItem?.id"
            [draftLocked]="saveInProgress || savedInThisForm()"
            [showAccessControl]="false"
            [showAssigneeControl]="!stitchItem?.id"
            assignLabel="Assignees"
            (draftAssigneesChange)="initialAssigneeIds.set($event)"
            (draftAccessChange)="initialTeamAccess.set($event)"
            (collaborationChange)="updateCollaborationState($event)"
            (accessLost)="collaborationAccessLost.set(true)"
          />
        }
        <ng-container [ngTemplateOutlet]="handoverButton" />
      </div>

      <div class="creation-toolbar-actions" [appPreventDirtyClick]="dirtyFormRef">
        <!-- Share and More options as in the saved note's header, greyed out until the note is saved (#2878). As there,
             a narrow window keeps only More options (its menu has Archive and Trash). -->
        @if (!contracted) {
          <stch-quick-action
            appearance="sapphire-toggle"
            size="m"
            icon="sapphire-archive2"
            [active]="form.controls.archived.value"
            [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
            [disabled]="!note?.id || contentReadOnly()"
            (onClick)="archive()"
          />
          <stch-trash-action
            [disabled]="!note?.id || contentReadOnly()"
            [deleted]="form.controls.deleted.value"
            (onClick)="delete()"
          />
          <stch-quick-action
            appearance="sapphire-toggle"
            size="m"
            icon="sapphire-share"
            matTooltip="Share"
            [disabled]="!note?.id || contentReadOnly()"
            [stchShareContextMenu]="note"
            [stchShareContextMenuDisabled]="!note?.id || contentReadOnly()"
          />
        }
        <app-button
          appearance="sapphire-secondary-ghost"
          size="xs"
          icon="sapphire-ellipsis"
          matTooltip="More options"
          ariaLabel="More options"
          [disabled]="!note?.id || (contentReadOnly() && (!spaceContextStore.insideTeam() || note.accountId !== spaceContextStore.contextId()))"
          [appNoteReadOnlyContextMenu]="!!note?.id && contentReadOnly()"
          [appNoteReadOnlyContextMenuId]="note?.id"
          [appNoteReadOnlyContextMenuNotes]="note ? [note] : []"
          appNoteReadOnlyContextMenuMode="popover"
          [appNoteReadOnlyContextMenuElementLocation]="UILocations.mainView"
          (appNoteContextMenuTab)="openItemComments()"
          [appNoteContextMenuElementLocation]="UILocations.mainView"
          [appNoteContextMenu]="note"
          [appNoteContextMenuDisabled]="!note?.id || contentReadOnly()"
          appNoteContextMenuMode="popover"
          [printEnabled]="true"
        />
      </div>
    </div>
  </div>
  <div class="note-timestamps flex items-center justify-between gap-2 pr-8">
    <app-stitch-timestamps class="block" [createdAt]="note?.createdAt" [updatedAt]="note?.updatedAt" />
    <app-item-team-label [revision]="workspaceRevision()" [identity]="displayWorkspaceIdentity()" [item]="note" />
    @if (headerNotebook()) {
      <ng-container [ngTemplateOutlet]="notebookBadge" />
    } @else if (locationBadge(); as badge) {
      <app-search-location-badge class="min-w-0 self-start -mt-[2px]" [type]="badge.type" [name]="badge.name ?? ''" />
    }
  </div>
  <div class="tabs">
    <app-tabs appearance="sapphire" [values]="visibleTabs()" [value]="selectedTab" (valueChange)="selectHistoryTab($event)" size="m" />
    <div class="filler"></div>
    <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
  </div>
} @else {
  <app-note-detail-header [actionsBesideTitle]="!spaceContextStore.insideTeam()" (resize)="handleResize($event)">
    <div noteHeaderIdentity>
      <!-- Team workspaces only: a personal note's notebook is set on its Details tab (stitch-app-shell skin). -->
      @if (spaceContextStore.insideTeam()) {
        <ng-container [ngTemplateOutlet]="notebookButton" />
      }
      @if (owningWorkspaceError()) {
        <span role="alert">Could not load note workspace.</span>
        <app-button2 appearance="secondary-text" size="s" (click)="retryOwningWorkspace.next()">Retry</app-button2>
      }
      @if (collaborationControlsTenantId()) {
        <app-note-collaboration-controls
          [noteId]="stitchItem?.id ?? null"
          [teamTenantId]="collaborationControlsTenantId()"
          [teamName]="collaborationTeamName()"
          [showAssigneeControl]="false"
          assignLabel="Assignees"
          (collaborationChange)="updateCollaborationState($event)"
          (accessLost)="collaborationAccessLost.set(true)"
        />
      }
      <ng-container [ngTemplateOutlet]="handoverButton" />
    </div>
    <div noteHeaderActions>
      <div class="contents" [appPreventDirtyClick]="dirtyFormRef">
        @if (!contracted) {
          <stch-quick-action
            appearance="sapphire-toggle"
            size="m"
            icon="sapphire-archive2"
            [active]="form.controls.archived.value"
            [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
            [disabled]="!note?.id || contentReadOnly()"
            (onClick)="archive()"
          />
          <stch-trash-action
            [disabled]="!note?.id || contentReadOnly()"
            [deleted]="form.controls.deleted.value"
            (onClick)="delete()"
          />
          <stch-quick-action
            appearance="sapphire-toggle"
            size="m"
            icon="sapphire-share"
            matTooltip="Share"
            [disabled]="!note?.id || contentReadOnly()"
            [stchShareContextMenu]="note"
            [stchShareContextMenuDisabled]="!note?.id || contentReadOnly()"
          />
        }
        <app-button
          appearance="sapphire-secondary-ghost"
          size="xs"
          icon="sapphire-ellipsis"
          matTooltip="More options"
          ariaLabel="More options"
          [disabled]="!note?.id || (contentReadOnly() && (!spaceContextStore.insideTeam() || note.accountId !== spaceContextStore.contextId()))"
          [appNoteReadOnlyContextMenu]="!!note?.id && contentReadOnly()"
          [appNoteReadOnlyContextMenuId]="note?.id"
          [appNoteReadOnlyContextMenuNotes]="note ? [note] : []"
          appNoteReadOnlyContextMenuMode="popover"
          [appNoteReadOnlyContextMenuElementLocation]="UILocations.mainView"
          (appNoteContextMenuTab)="openItemComments()"
          [appNoteContextMenuElementLocation]="UILocations.mainView"
          [appNoteContextMenu]="note"
          [appNoteContextMenuDisabled]="!note?.id || contentReadOnly()"
          appNoteContextMenuMode="popover"
          [printEnabled]="true"
        />
      </div>
      @if (showCloseButton) {
        <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" ariaLabel="Close" (click)="handleClose()" />
      }
    </div>
    <!-- Back sits before the Note tile, as the skin's detail header draws it (Johanna, 2026-10-20). -->
    <div noteHeaderIcon class="note-header-lead">
      @if (canGoBack()) {
        <app-button2 appearance="secondary-ghost" size="s" ariaLabel="Go back" (click)="handleGoBack()">
          <app-icon name="chevron-left" />
        </app-button2>
      }
      <span class="note-header-icon" aria-hidden="true"><app-icon name="sapphire-note" [size]="22" /></span>
    </div>
    <app-input
      noteHeaderTitle
      class="skin-detail-title"
      appearance="sapphire-inline"
      [inputFormControl]="form.controls.title"
      placeholder="New Note"
      [errorPlaceholder]="'Add title'"
      [withErrors]="submitted"
      size="m"
    />
    <!-- Too narrow for it all: the dates end in "…" first, then the badge, which keeps at least a 56px stub. -->
    <div noteHeaderDates class="note-timestamps flex min-w-0 items-center justify-between gap-2">
      <app-stitch-timestamps class="block min-w-0 flex-[1_1_auto]" [createdAt]="note?.createdAt" [updatedAt]="note?.updatedAt" />
      <app-item-team-label [revision]="workspaceRevision()" [identity]="displayWorkspaceIdentity()" [item]="note" />
      @if (headerNotebook()) {
        <ng-container [ngTemplateOutlet]="notebookBadge" />
      } @else if (locationBadge(); as badge) {
        <app-search-location-badge class="min-w-0 self-start -mt-[2px]" [type]="badge.type" [name]="badge.name ?? ''" />
      }
    </div>
    <div noteHeaderTabs class="flex min-w-0 items-center gap-4">
      <app-tabs appearance="sapphire" [values]="visibleTabs()" [value]="selectedTab" (valueChange)="selectHistoryTab($event)" size="m" />
      @if (historyAvailable()) {
        <button type="button" data-note-history [attr.aria-pressed]="selectedTab === 'versions'" [disabled]="historyRestorePending()" (click)="openVersionHistory(note)">Version history</button>
      }
    </div>
    <stch-knowledge-button noteHeaderKnowledge [active]="displayKnowledge" (click)="showKnowledge()" />
  </app-note-detail-header>
}

<app-item-handover
  #handoverControl
  [api]="handoverApi"
  [enabled]="!!handoverTenantId()"
  [showButton]="false"
  [itemId]="note?.id"
  [tenantId]="handoverTenantId()"
  [insideModal]="handoverInsideModal()"
  [stitchType]="StitchType.NOTE"
  [prototypeWindow]="true"
  noun="note"
/>
<app-item-review
  [api]="reviewApi"
  [enabled]="!!handoverTenantId()"
  [itemId]="note?.id"
  [tenantId]="handoverTenantId()"
  [insideModal]="handoverInsideModal()"
  [stitchType]="StitchType.NOTE"
  [contentVersion]="reviewContentVersion()"
/>

<ng-template #handoverButton>
  @if (handoverTenantId()) {
    <app-button2
      appearance="secondary"
      size="m"
      [height]="32"
      rounding="lg"
      data-handover-anchor
      [ariaLabel]="handoverControl.accessibleName()"
      [ariaHaspopup]="'dialog'"
      [disabled]="!canAssignHandover() || handoverControl.busy()"
      [attr.title]="canAssignHandover() ? null : 'Requires edit access'"
      [ariaDescribedBy]="
        !canAssignHandover() ? handoverControl.ids.title + '-edit' : handoverControl.busy() ? handoverControl.ids.busy : null
      "
      (click)="canAssignHandover() && handoverControl.openAssign()"
    >
      <app-icon name="sapphire-user-plus" size="m" />
      {{ handoverControl.buttonLabel() }}
      <app-icon name="sapphire-chevron-down" size="s" />
      <span class="sr-only" [id]="handoverControl.ids.title + '-edit'">{{ canAssignHandover() ? '' : 'Requires edit access' }}</span>
    </app-button2>
  }
</ng-template>

@if (form) {
  <div
    class="form"
    appDroppable
    appDroppableHoverClass="drag-over"
    [appDroppableDisabled]="!stitchItem?.id || contentReadOnly()"
    [appDroppablePredicate]="dndPredicate"
    [formGroup]="form"
    (appDroppableDrop)="dndDrop($event)"
  >
    <div class="drop-area-border"></div>

    @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
      <app-entity-hub-search [contactId]="note?.id" />
    }

    <div class="form-heading">
      <div class="form-heading-nav">
        <div class="form-heading-nav-separator"></div>

      </div>

      @if (displayKnowledge) {
        <div class="form-heading-nav-underline"></div>
      }

      <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. On the Stitch tab the rows are
           the knowledge hub's facets (#2481), read-only, and hidden without hub access. -->
      <div
        class="form-knowledge"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
      >
        <app-knots
          class="form-knots"
          [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="note | toArray"
          [tenantId]="note.accountId ?? sharingTenantId()"
          [control]="form.controls['knots']"
          [stateKey]="StateKey.knotsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="note?.id"
        />

        <app-tags
          class="form-tags"
          [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="[note]"
          [tenantId]="note.accountId ?? sharingTenantId()"
          [control]="form.controls['tags']"
          [stateKey]="StateKey.tagsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="note?.id"
        />

        <app-connections
          class="form-knots"
          [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
          [stitchItems]="[note]"
          [control]="form.controls['connections']"
          [stateKey]="StateKey.connectionsList"
          [withControls]="selectedTab !== 'stitch'"
          [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
          [hubEntityUid]="note?.id"
        />
      </div>
    </div>

    @if ((selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))) {
      @if (historySaveRequired()) {
        <section class="p-6" aria-label="Save before version history">
          <p>Save your changes before opening history or restoring content.</p>
          @if (historySaveFailure()) { <p role="alert">{{ historySaveFailure() }}</p> }
          <app-button2 appearance="secondary" #historySaveCancel [disabled]="editorSubmitInProgress() || saveInProgress" (click)="cancelHistorySave(true)">Cancel</app-button2>
          <app-button2 appearance="primary" [disabled]="editorSubmitInProgress() || saveInProgress" (click)="saveBeforeHistory()">Save</app-button2>
        </section>
      }
      <app-scroll class="form-scroll" (top)="displayScrollShadow = !$event">
        @if (displayScrollShadow) {
          <div class="form-scroll-shadow"></div>
        }

        @switch (selectedTab) {
          @case ('note') {
            <div class="form-editor min-h-80">
              <app-editor
                formControlName="body"
                ariaLabel="Note content"
                [fitBlockMenus]="true"
                [readOnly]="contentReadOnly()"
                [customActions]="editorActions"
                [highlights]="highlights()"
                [tenantItems]="tenantCarrier()"
                (contentEdited)="handleEditorContentEdited()"
                (attachmentUploaded)="handleEditorUploadedAttachment($event)"
                (uploading)="onUploadingChange($event)"
              />
            </div>
          }

          @case ('details') {
            <div class="form-kit" [class.contracted]="contracted">
              <section class="form-kit-section">
                <div class="form-kit-field">
                  <span class="form-kit-label">Notebook</span>

                  <app-autocomplete-input
                    appearance="sapphire-outline"
                    placeholder="Notebook"
                    [suggestions]="suggestions"
                    [inputFormControl]="form.controls.notebookId"
                    size="s"
                    [withErrors]="submitted"
                    [withClear]="true"
                    (onSelection)="handleDetailsNotebookSelection($event)"
                  />
                </div>
              </section>

              <section class="form-kit-section">
                <stch-item-description-field
                  [control]="form.controls.description"
                  [item]="note"
                  [tagsControl]="form.controls['tags']"
                  [knotsControl]="form.controls['knots']"
                />
              </section>

              <!-- Team workspace handover (main): who answers for the note and who wrote it, as a read-only InfoList. -->
              @if (handoverTenantId()) {
                <section class="form-kit-section">
                  <dl class="form-kit-info">
                    <div class="form-kit-info-row">
                      <dt>Responsible</dt>
                      <dd data-testid="details-responsible">{{ handoverControl.responsibleName() }}</dd>
                    </div>
                    @if (handoverControl.creatorName(); as author) {
                      <div class="form-kit-info-row">
                        <dt>Created by</dt>
                        <dd data-testid="details-authored-by">{{ author }}</dd>
                      </div>
                    }
                  </dl>
                </section>
              }
            </div>
          }

          @case ('hub') {
            @if (!permissionsLoaded()) {
              <!-- Render nothing until permission rules arrive to avoid flashing the wrong pane -->
            } @else if (hasKnowledgeHubAccess()) {
              <app-knowledge-hub [item]="note" />
            } @else {
              <app-feature-upsell
                title="Knowledge Hub"
                description="AI-powered summaries, entities, themes, and key terms for your items. Upgrade your plan to unlock Knowledge Hub."
                imageSrc="assets/placeholders/placeholder-files.svg"
              />
            }
          }

          @case ('attachments') {
            <app-attachments-list
              class="form-attachments"
              [stitchItem]="stitchItem"
              [tenantItems]="tenantCarrier()"
              [inputControl]="form.controls.uploads"
              [contextMenuStateVariant]="attachmentsMenuStateVariant"
              (listStateChange)="attachmentsListState = $event"
              (onClickItem)="handleClickItem($event)"
              (onDblClickItem)="handleDblClickItem($event)"
              (uploading)="onUploadingChange($event)"
            />
          }

          @case ('stitch') {
            <app-context-menu-state-indicator
              class="block ml-auto w-fit mt-5 mr-4"
              svgIcon="filters"
              appStitchListContextMenu
              (appStitchListContextMenuChange)="stitchListState = $event"
            />

            <app-linked-info-list
              class="form-linked-info"
              [stitch]="note"
              [state]="stitchListState"
              (clickStitchItem)="handleClickItem($event)"
              (dblClickStitchItem)="handleDblClickItem($event)"
            />
          }

          @case ('comments') {
            <stch-comments (countChanged)="commentsCount.set($event)" [compactNormalRoot]="true" [insideTeam]="insideTeam" class="form-linked-info notes-compact-normal-root" [stitch]="note" />
          }

          @case ('versions') {
            @if (historyAvailable()) {
              <app-note-version-history [note]="note" [canEdit]="!contentReadOnly()"
                [saving]="editorSubmitInProgress() || saveInProgress"
                [prepareRestore]="prepareHistoryRestore" (restoring)="historyRestorePending.set($event)"
                (refreshed)="applyHistoryNote($event)" />
            }
          }

          @case ('activity') {
            @if (stitchItem?.id && collaborationControlsTenantId()) {
              <app-note-activity [noteId]="stitchItem.id" [tenantId]="stitchItem.accountId" />
            }
          }

          @case ('debug') {
            <stch-json-viewer [json]="note?.debug" />
          }
        }

        <app-item-quick-comment
          [revision]="workspaceRevision()"
          [identity]="displayWorkspaceIdentity()"
          [item]="note"
          noun="note"
          [selectedTab]="selectedTab"
          [openComments]="openItemComments"
        />
      </app-scroll>
    }

    @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
      <app-stitch-hub
        [stitch]="note"
        (clickStitchItem)="handleClickItem($event)"
        (dblClickStitchItem)="handleDblClickItem($event)"
      />
    }

    @if (!contentReadOnly()) {
      <stch-save-cancel-panel
        [class.note-create-save-panel]="creationJourney()"
        [enabled]="!pristine || editorSubmitInProgress()"
        [uploading]="isUploading() || saveInProgress || editorSubmitInProgress()"
        [saveLabel]="saveLabel()"
        (onCancel)="handleCancel()"
        (onSave)="handleCreateOrSave()"
      />
    }

  </div>
}

<ng-template #saveLocationMenu>
  @if (workspaceIdentity(); as identity) {
    <div
      class="save-location-menu"
      role="dialog"
      aria-label="Save location"
      (keydown.escape)="closeSaveLocationPanel(); $event.stopPropagation(); $event.preventDefault()"
    >
      @if (!destinationLocked()) {
        <div class="save-location-destinations" role="menu" aria-label="Note destination">
          <div class="save-location-menu-heading">Save location</div>

          <button
            type="button"
            role="menuitemradio"
            [attr.aria-checked]="!destinationIsTeam()"
            class="save-location-menu-row"
            [class.save-location-menu-row-selected]="!destinationIsTeam()"
            [disabled]="saveInProgress"
            (click)="selectDestination(identity.memberTenantId)"
          >
            <app-icon name="sapphire-lock" />
            <span class="save-location-menu-copy">
              <span class="save-location-menu-name">My Workspace</span>
              <span class="save-location-menu-subtitle">Private to you. Nobody else can see it</span>
            </span>
            @if (!destinationIsTeam()) {
              <app-icon class="save-location-menu-check" name="sapphire-check" size="s" />
            }
          </button>

          <button
            type="button"
            role="menuitemradio"
            [attr.aria-checked]="destinationIsTeam()"
            class="save-location-menu-row"
            [class.save-location-menu-row-selected]="destinationIsTeam()"
            [disabled]="saveInProgress"
            (click)="selectDestination(identity.spaceTenantId)"
          >
            <span class="save-location-menu-team-avatar">{{ teamInitials() }}</span>
            <span class="save-location-menu-copy">
              <span class="save-location-menu-name">{{ identity.spaceTitle }}</span>
              <span class="save-location-menu-subtitle">Everyone at {{ identity.spaceTitle }} will see your note</span>
            </span>
            @if (destinationIsTeam()) {
              <app-icon class="save-location-menu-check" name="sapphire-check" size="s" />
            }
          </button>
        </div>
      }
      @if (collaborationControlsTenantId()) {
        <app-note-access-panel
          [view]="accessPanelView()"
          [teamName]="collaborationTeamName()"
          [focusOnOpen]="false"
          (accessChange)="collaborationControls()?.changeAccess($event)"
          (retry)="collaborationControls()?.retry()"
          (close)="closeSaveLocationPanel()"
        />
      } @else {
        <div class="save-location-private-access">
          <div class="save-location-menu-heading">Access</div>
          <div class="save-location-menu-access">
            @if (!destinationIsTeam()) {
              <app-icon name="sapphire-lock" />
            } @else {
              <span class="save-location-menu-team-avatar">{{ teamInitials() }}</span>
            }
            <span>{{ accessLabel() }}</span>
          </div>
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #notebookMenu>
  <div class="notebook-menu" role="menu" aria-label="Notebook">
    <button
      type="button"
      role="menuitemradio"
      [attr.aria-checked]="!form.controls.notebookId.value"
      class="notebook-menu-row"
      [class.notebook-menu-row-selected]="!form.controls.notebookId.value"
      (click)="selectDefaultNotebook()"
    >
      <app-icon name="sapphire-notebook" />
      <span>{{ defaultNotebookLabel() }}</span>
      @if (!form.controls.notebookId.value) {
        <app-icon class="notebook-menu-check" name="sapphire-check" size="s" />
      }
    </button>

    <div class="notebook-menu-divider"></div>

    @for (notebook of notebookOptions(); track notebook.id) {
      <button
        type="button"
        role="menuitemradio"
        [attr.aria-checked]="form.controls.notebookId.value === notebook.id"
        class="notebook-menu-row"
        [class.notebook-menu-row-selected]="form.controls.notebookId.value === notebook.id"
        (click)="selectNotebook(notebook)"
      >
        <app-icon name="sapphire-notebook" />
        <span class="notebook-menu-name">{{ notebook.title || 'Untitled' }}</span>
        @if (form.controls.notebookId.value === notebook.id) {
          <app-icon class="notebook-menu-check" name="sapphire-check" size="s" />
        }
      </button>
    }

    <button type="button" role="menuitem" class="notebook-menu-add" (click)="showNewNotebookDialog()">
      <app-icon name="sapphire-plus" size="s" />
      Add new notebook
    </button>
  </div>
</ng-template>

<!-- The note's notebook: the creation header's picker, and the same control (locked) on a reopened note's header (#1988). -->
<ng-template #notebookButton>
  <app-button2
    class="creation-toolbar-picker"
    appearance="secondary"
    size="m"
    [height]="32"
    rounding="lg"
    [disabled]="locationControlsLocked()"
    [ariaLabel]="notebookButtonLabel()"
    [style.width.px]="notebookButtonWidth()"
    [style.--color-border]="notebookMenuOpen() ? '#1b5fff' : null"
    stchPopover
    stchPopoverTrigger="click"
    [stchPopoverTemplate]="notebookMenu"
    stchPopoverPlacement="bottomLeft"
    [stchPopoverOffsetY]="2"
    [stchPopoverCloseOnOutsideClick]="true"
    [stchPopoverShowUntil]="closeNotebookMenu"
    [stchPopoverDisabled]="locationControlsLocked()"
    [stchPopoverNoBorder]="true"
    (stchPopoverVisibleChange)="handleNotebookMenuVisible($event)"
  >
    <app-icon name="sapphire-notebook" />
    {{ notebookButtonLabel() }}
    <app-icon name="sapphire-chevron-down" />
  </app-button2>
</ng-template>

<ng-template #newNotebookDialog>
  <div class="new-notebook-dialog" role="dialog" aria-labelledby="new-notebook-title">
    <div class="new-notebook-dialog-heading">
      <div id="new-notebook-title">New Notebook</div>
      <app-button2
        appearance="secondary-ghost"
        size="s"
        ariaLabel="Close New Notebook"
        [disabled]="notebookCreateInProgress()"
        (click)="cancelNewNotebook()"
      >
        <app-icon name="sapphire-close" />
      </app-button2>
    </div>

    <app-input
      class="new-notebook-dialog-input"
      appearance="sapphire-outline"
      size="s"
      placeholder="Untitled notebook"
      [inputFormControl]="newNotebookTitle"
      [withErrors]="false"
      [focusImmediately]="true"
    />

    <div class="new-notebook-dialog-footer">
      <app-button2
        appearance="secondary"
        size="s"
        [disabled]="notebookCreateInProgress()"
        (click)="cancelNewNotebook()"
      >
        Cancel
      </app-button2>
      <app-button2 appearance="primary" size="s" [disabled]="newNotebookSaveDisabled()" (click)="createNewNotebook()">
        Save
      </app-button2>
    </div>
  </div>
</ng-template>

<!-- stitch-app-shell breadcrumbs footer in the main view, as in every app; elsewhere the workspace footer. -->
@if (navigation.enabled && note?.id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
} @else {
  <app-item-workspace-footer
    [revision]="workspaceRevision()"
    [identity]="displayWorkspaceIdentity()"
    [item]="note"
    noun="Notes"
    [collection]="footerBadge()?.name"
    [archived]="form.controls.archived.value"
    [deleted]="form.controls.deleted.value"
  />
}

} @else {
  <p role="status" class="p-4 text-sapphire-grey-2">
    @if (noteContentState() === 'error') {
      The note could not be loaded. Reopen it to try again.
    } @else {
      Loading note…
    }
  </p>
}

<!-- stitch-app-shell FolderBadge: the note's notebook in its colour. -->
<ng-template #notebookBadge>
  @if (headerNotebook(); as notebook) {
    <app-container-badge
      type="notebook"
      class="min-w-[56px] shrink self-start -mt-[2px]"
      [title]="notebook.title"
      [color]="notebook.color"
    />
  }
</ng-template>
